import { getTokenHeader } from '@/client/config'
import { useEffect, useRef } from 'react'
import MonacoEditor, { Monaco } from '@monaco-editor/react'
import { CONVERTER_DATA_LIB, withConverterData } from '@client/views/converter/converterData'
import { accessToken } from '@client/stores/authStore'

const defaultEditorContent = `// not implemented yet`

const globalScriptUrl = 'modules/global.d.ts'
//const globalDeclarionsUrl = 'global.d.ts'
const globalDeclarionsUrl = 'node_modules/@types/global/index.d.ts'

const size = 16
const options = {
    readOnly: false,
    fontSize: size,
    lineHeight: size * 1.4,
    minimap: {
        enabled: false,
    },
}

export type EditorProps = {
    className: string,
    code: string,
    readOnly?: boolean,
    onChange: (value: string | undefined, ev: any) => void,
    onValidate?: (errors: any[], warnings: any[], markers: any[]) => void,
    /** the fields args.data offers in this editor (see views/converter/converterData) */
    dataDeclaration?: string,
}

// Obsolete: Funktion zur Konvertierung von TypeScript-Code in eine Typ-Definition (.d.ts)
// function convertTsToDts(tsCode: string): string {
//     const sourceFile = ts.createSourceFile("temp.ts", tsCode, ts.ScriptTarget.Latest, true);
//     const dtsLines: string[] = [];

//     function visit(node: ts.Node) {
//         if (ts.isFunctionDeclaration(node) && node.name) {
//             dtsLines.push(`declare function ${node.name.getText()}(): string;`);
//         } else if (ts.isVariableStatement(node)) {
//             node.declarationList.declarations.forEach(declaration => {
//                 if (ts.isIdentifier(declaration.name)) {
//                     dtsLines.push(`declare const ${declaration.name.getText()}: () => string;`);
//                 }
//             });
//         } else if (ts.isTypeAliasDeclaration(node)) {
//             dtsLines.push(node.getText());
//         } else if (ts.isClassDeclaration(node) && node.name) {
//             const className = node.name.getText();
//             const isAbstract = node.modifiers?.some(mod => mod.kind === ts.SyntaxKind.AbstractKeyword) ? 'abstract ' : '';
//             let classContent = `declare ${isAbstract}class ${className} `;

//             if (node.heritageClauses) {
//                 const extendsClause = node.heritageClauses.find(hc => hc.token === ts.SyntaxKind.ExtendsKeyword);
//                 if (extendsClause) {
//                     const baseClass = extendsClause.types.map(t => t.getText()).join(', ');
//                     classContent += `extends ${baseClass} `;
//                 }
//             }

//             classContent += '{\n';

//             node.members.forEach(member => {
//                 if (ts.isMethodDeclaration(member) && member.name) {
//                     const methodName = member.name.getText();
//                     const isAbstractMethod = member.modifiers?.some(mod => mod.kind === ts.SyntaxKind.AbstractKeyword) ? 'abstract ' : '';
//                     const parameters = member.parameters.map(param => {
//                         const paramName = param.name.getText();
//                         const paramType = param.type ? param.type.getText() : 'any';
//                         return `${paramName}: ${paramType}`;
//                     }).join(', ');
//                     const returnType = member.type ? member.type.getText() : 'void';
//                     classContent += `  ${isAbstractMethod}${methodName}(${parameters}): ${returnType};\n`;
//                 } else if (ts.isPropertyDeclaration(member) && member.name) {
//                     const propName = member.name.getText();
//                     const propType = member.type ? member.type.getText() : 'any';
//                     classContent += `  ${propName}: ${propType};\n`;
//                 }
//             });

//             classContent += '}';
//             dtsLines.push(classContent);
//         }
//         ts.forEachChild(node, visit);
//     }

//     ts.forEachChild(sourceFile, visit);
//     return dtsLines.join('\n');
// }

export default function Editor({ className, code, onChange, onValidate, readOnly, dataDeclaration }: EditorProps) {
    if (!code) code = defaultEditorContent
    if (readOnly) options.readOnly = readOnly

    // Monaco keeps one set of declarations for all editors: with two converter
    // tabs open, the one in front has to bring its own fields back.
    const monacoRef = useRef<Monaco | null>(null)
    const declarationRef = useRef(dataDeclaration)
    declarationRef.current = dataDeclaration
    const applyDeclaration = () => {
        const monaco = monacoRef.current
        if (!monaco || declarationRef.current === undefined) return
        monaco.languages.typescript.typescriptDefaults.addExtraLib(declarationRef.current, CONVERTER_DATA_LIB)
    }
    useEffect(applyDeclaration, [dataDeclaration])

    const handleEditorMount = (editor: any) => {
        applyDeclaration()
        editor.onDidFocusEditorText(applyDeclaration)
    }

    const handleEditorInit = (monaco: Monaco) => {
        monacoRef.current = monaco
        monaco.languages.typescript.javascriptDefaults.setEagerModelSync(true)

        monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: false,
            noSyntaxValidation: false
        })

        // compiler options
        monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
            ...monaco.languages.typescript.typescriptDefaults.getCompilerOptions(),
            moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
            isolatedModules: true,
            allowSyntheticDefaultImports: true,

            typeRoots: ["node_modules/@types"],
            allowNonTsExtensions: true,
        });

        var headers = new Headers();
        headers.set('Accept', 'text/html')
        headers.set('Cache-Control', 'no-cache')
        headers.set('Pragma', 'no-cache')
        headers.set('Expires', '0')
        headers.set('Authorization', accessToken.value ? `Bearer ${accessToken.value}` : '')

        if (!monaco.languages.typescript.typescriptDefaults.getExtraLibs()[globalDeclarionsUrl]) {
            fetch(globalScriptUrl, { headers })
                .then(response => response.text())
                .then(scriptContent => {
                    try {
                        // Konvertiere global.ts zu global.d.ts
                        //const dtsContent = convertTsToDts(scriptContent)
                        // args.data as ConverterData, for the fields to be suggested
                        const dtsContent = withConverterData(scriptContent)

                        // TypeScript-Erweiterungen mit neuen globalen Typen hinzufügen
                        monaco.languages.typescript.typescriptDefaults.addExtraLib(dtsContent, globalDeclarionsUrl)
                    } catch (error) {
                        console.error("Fehler beim Laden und Konvertieren von global.ts:", error)
                    }
                })
        }
    }

    function handleValidate(markers: any[]): void {
        // codes
        // 6000 = warnings
        // 6133 'xxx' is declared but its value is never read
        // 2000 = errors
        // 2339 Property 'xxx' does not exost on type 'ConverterProps'.

        const warnings: any[] = []
        const errors: any[] = []
        markers.forEach(marker => {
            if (marker.code >= 2000 && marker.code < 2999) errors.push(marker)
            if (marker.code >= 6000 && marker.code < 6999) warnings.push(marker)
        })

        if (onValidate) onValidate(errors, warnings, markers)
    }

    return (<div className={className}>
        <MonacoEditor
            height="80vh"
            theme="vs-dark"
            options={options}
            value={code}
            defaultLanguage='typescript'
            beforeMount={handleEditorInit}
            onMount={handleEditorMount}
            onChange={onChange}
            onValidate={handleValidate}
        />
    </div>)
}
